import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api";

/**
 * Carga de datos para las pantallas de la consola.
 *
 * Deliberadamente chico y SIN refetch automatico por intervalo: el sistema no
 * hace polling (ADR 0006, invariante 11). Los datos se piden al entrar a la
 * pantalla y despues de cada mutacion, con `recargar()`. Un `setInterval` "por
 * las dudas" impide que Neon suspenda, que es donde el sistema paga.
 *
 * `clave` es lo que hace que se vuelva a cargar: una cadena que cambia cuando
 * cambian los filtros. Va como string y no como array de dependencias porque un
 * array armado en el llamador es invisible para el linter de hooks — y ahi los
 * refetch que faltan no fallan, simplemente muestran datos viejos.
 */
/**
 * Si la respuesta que acaba de llegar es la de la ULTIMA carga pedida.
 *
 * Sin esto, dos consultas en vuelo se pisan por orden de llegada: se abre la
 * pantalla, sale la consulta sin filtro, se escribe un filtro y sale la
 * segunda. Si la primera tarda mas, su `setDatos` gana y la tabla muestra TODO
 * con el filtro puesto — sin error, sin nada raro, solo el resultado
 * equivocado. Aparecio en el e2e de auditoria: 49 filas despues de filtrar.
 */
export function esLaUltima(contador: { actual: number }, mia: number): boolean {
  return contador.actual === mia;
}

export function useDatos<T>(cargar: () => Promise<T>, clave = "") {
  const [datos, setDatos] = useState<T | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // La funcion se recrea en cada render del llamador; la ref evita que eso
  // dispare una recarga por si solo.
  const cargarRef = useRef(cargar);
  cargarRef.current = cargar;

  // Cual carga es la vigente. Va en una ref y no en estado: cambiarlo no tiene
  // que re-renderizar, solo decidir quien puede escribir el resultado.
  const contador = useRef({ actual: 0 });

  const recargar = useCallback(async () => {
    contador.current.actual += 1;
    const mia = contador.current.actual;
    setCargando(true);
    try {
      const resultado = await cargarRef.current();
      // Una respuesta que quedo atras no escribe: ya salio otra despues.
      if (!esLaUltima(contador.current, mia)) return;
      setDatos(resultado);
      setError(null);
    } catch (err) {
      if (!esLaUltima(contador.current, mia)) return;
      setError(err instanceof ApiError ? err.message : "No se pudieron cargar los datos");
    } finally {
      if (esLaUltima(contador.current, mia)) setCargando(false);
    }
  }, []);

  // La regla trata a `clave` como valor de scope externo porque es un parametro
  // del hook. No lo es: cuando el llamador pasa otra clave, este hook
  // re-renderiza y el efecto TIENE que volver a correr — es la unica forma de
  // que cambiar un filtro recargue los datos. Sacarla deja la pantalla
  // mostrando el resultado viejo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ver arriba
  useEffect(() => {
    void recargar();
  }, [recargar, clave]);

  return { datos, cargando, error, recargar, setError };
}
